
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Post {
  id: string;
  title: string;
  content: string;
  created_at: string;
}

export function CommunitySection() {
  const [recentPosts, setRecentPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentPosts();
  }, []);

  const fetchRecentPosts = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/posts`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // 최근 3개 게시글만 가져오기
        setRecentPosts(data.slice(0, 3));
      }
    } catch (error) {
      console.error('게시글을 불러오는데 실패했습니다:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '오늘';
    if (diffDays === 2) return '1일 전';
    return `${diffDays - 1}일 전`;
  };

  const truncateContent = (content: string, maxLength: number = 50) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <section id="community" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold text-gray-900 mb-4">
            라코스벨 커뮤니티
          </h3>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            라코스벨과 함께하는 고객들의 생생한 이야기와 경험을 공유하는 공간입니다
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* 왼쪽: 커뮤니티 소개 */}
          <div>
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-chat-3-line text-xl text-emerald-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">실제 사용 후기</h4>
                  <p className="text-gray-600">
                    라코스벨 제품을 직접 사용해보신 고객들의 솔직한 후기와 경험담을 확인해보세요
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-image-line text-xl text-emerald-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">사용법 공유</h4>
                  <p className="text-gray-600">
                    제품을 더욱 효과적으로 사용하는 방법과 팁들을 서로 나누어보세요
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-line text-xl text-emerald-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">소통과 교류</h4>
                  <p className="text-gray-600">
                    라코스벨을 사랑하는 사람들과 함께 아름다운 이야기를 만들어가세요
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/board"
                className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2 inline-block">
                  <i className="ri-article-line"></i>
                </div>
                게시판 보기
              </Link>
              <Link
                to="/board"
                className="border-2 border-emerald-600 text-emerald-600 px-8 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2 inline-block">
                  <i className="ri-edit-line"></i>
                </div>
                글 작성하기
              </Link>
            </div>
          </div>

          {/* 오른쪽: 커뮤니티 이미지 */}
          <div className="relative">
            <div 
              className="w-full h-96 bg-cover bg-center rounded-2xl shadow-lg"
              style={{
                backgroundImage: `url('https://public.readdy.ai/ai/img_res/bc0d4b47c018f45ca10cadb6984ec3e7.jpg')`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <h5 className="text-xl font-semibold mb-2">  </h5>
                <p className="text-white/90">  </p>
              </div>
            </div>

            {/* 플로팅 카드들 */}
            <div className="absolute -top-4 -right-4 bg-white rounded-lg shadow-lg p-4 max-w-48">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-emerald-600 text-sm"></i>
                </div>
                <span className="text-sm font-medium text-gray-900">김○○님</span>
              </div>
              <p className="text-xs text-gray-600">"정말 향이 좋아요! 자연스럽고 은은한 향이 하루종일 기분을 좋게 해줍니다"</p>
            </div>

            <div className="absolute -bottom-4 -left-4 bg-white rounded-lg shadow-lg p-4 max-w-48">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-emerald-600 text-sm"></i>
                </div>
                <span className="text-sm font-medium text-gray-900">박○○님</span>
              </div>
              <p className="text-xs text-gray-600">"피부가 정말 부드러워졌어요. 자연 성분이라 안심하고 사용할 수 있어서 좋습니다"</p>
            </div>
          </div>
        </div>

        {/* 최근 게시글 미리보기 */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-2xl font-bold text-gray-900">최근 커뮤니티 소식</h4>
            <Link
              to="/board"
              className="text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer"
            >
              더보기 →
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-gray-600">로딩 중...</div>
            </div>
          ) : recentPosts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-400">
                <i className="ri-file-text-line text-4xl"></i>
              </div>
              <h5 className="text-lg font-semibold text-gray-600 mb-2">아직 게시글이 없습니다</h5>
              <p className="text-gray-500 mb-4">첫 번째 게시글을 작성해보세요!</p>
              <Link
                to="/board"
                className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                글 작성하기
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recentPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/board/${post.id}`}
                  className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-emerald-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-gray-600">작성자</span>
                    <span className="text-xs text-gray-400">{formatDate(post.created_at)}</span>
                  </div>
                  <h5 className="font-semibold text-gray-900 mb-2">{post.title}</h5>
                  <p className="text-sm text-gray-600">{truncateContent(post.content)}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
